
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, Dumbbell } from 'lucide-react';
import { TimePeriod, TrainingSession } from '../types';
import { isSameDay, isWithinInterval, parseISO } from 'date-fns';
import { getBoundariesForPeriod } from '../utils/chart/boundaries';

interface TrainingStatsProps {
  sessions: TrainingSession[] | null;
  selectedPeriod: TimePeriod;
  onPeriodChange?: (period: TimePeriod) => void;
}

export function TrainingStats({ sessions, selectedPeriod, onPeriodChange }: TrainingStatsProps): JSX.Element {
  const stats = useMemo(() => {
    if (!sessions || !Array.isArray(sessions)) {
      return {
        sessionsCount: 0,
        totalHours: 0,
        averageSessionLength: 0
      };
    }

    const { currentStart, currentEnd } = getBoundariesForPeriod(selectedPeriod);
    
    const filteredSessions = selectedPeriod === 'all' 
      ? sessions 
      : sessions.filter((session: TrainingSession) => {
          try {
            const sessionDate = parseISO(session.date);
            
            const isInPeriod = isWithinInterval(sessionDate, {
              start: currentStart,
              end: currentEnd
            }) || isSameDay(sessionDate, currentStart) || isSameDay(sessionDate, currentEnd);
            
            return isInPeriod;
          } catch (e) {
            console.error('Error parsing date:', session.date, e);
            return false;
          }
      });
    
    const sessionsCount: number = filteredSessions.length;
    
    const totalMinutes: number = filteredSessions.reduce((total: number, session: TrainingSession) => {
      return total + (session.duration || 0);
    }, 0);
    
    const totalHours: number = Math.round(totalMinutes / 60 * 10) / 10;
    
    const averageSessionLength: number = sessionsCount > 0 
      ? Math.round((totalMinutes / sessionsCount) / 60 * 10) / 10 
      : 0;
    
    return {
      sessionsCount,
      totalHours,
      averageSessionLength
    };
  }, [sessions, selectedPeriod]);
  
  const periodLabel: string = useMemo(() => {
    if (selectedPeriod === 'week') {
      return 'This Week';
    } else if (selectedPeriod === 'month') {
      return 'This Month';
    } else if (selectedPeriod === 'year') {
      return 'This Year';
    } else if (selectedPeriod === 'all') {
      return 'All Time';
    }
    return 'Selected Period';
  }, [selectedPeriod]);

  const handleCardClick = (): void => {
    if (!onPeriodChange) return;
    
    const nextPeriod = (): TimePeriod => {
      switch (selectedPeriod) {
        case 'week': return 'month';
        case 'month': return 'year';
        case 'year': return 'all';
        case 'all': return 'week';
        default: return 'month';
      }
    };
    
    onPeriodChange(nextPeriod());
  };

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-4">
      <StatCard 
        icon={<Dumbbell className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />}
        value={stats.sessionsCount}
        label={`Sessions ${periodLabel}`}
        onClick={handleCardClick}
        interactive={!!onPeriodChange}
      />
      <StatCard 
        icon={<Clock className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />}
        value={stats.totalHours}
        label={`Hours ${periodLabel}`}
        onClick={handleCardClick}
        interactive={!!onPeriodChange}
      />
      <StatCard 
        icon={<Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />}
        value={stats.averageSessionLength}
        label="Avg. Hours Per Session"
        onClick={handleCardClick}
        interactive={!!onPeriodChange}
      />
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  onClick?: () => void;
  interactive?: boolean;
}

function StatCard({ icon, value, label, onClick, interactive }: StatCardProps): JSX.Element {
  return (
    <Card 
      onClick={onClick} 
      className={interactive ? "cursor-pointer active:scale-95 transition-transform" : ""}
    >
      <CardContent className="p-2 sm:p-4 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left">
        <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-md bg-primary/10 flex items-center justify-center mb-1 sm:mb-0 sm:mr-3">
          {icon}
        </div>
        <div>
          <p className="text-base sm:text-xl font-semibold">{value}</p>
          <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
